import { api, readCache } from '../api'
import { openDayMap } from '../day-map-panel'
import { isContact, isSuggestion } from '../planning'
import { diaryDayOrder } from '../diary-stops'
import { severalJobsOn } from '../job-names'
import { routeGen } from '../route-state'
import { clear, dayLabel, el, loadError } from '../ui'
import type { Clean, SchedulePayload } from '../types'

// The day's map, as a PAGE of its own at #/map/yyyy-mm-dd.
//
// Liam, 21 September 2026: "when I click the pin button, should that open in a
// new tab so that you could be juggling both at the same time? You can carry
// on sending messages and stuff, but... and be able to see the map?"
//
// Yes. The map used to be a panel laid over the diary with the page behind it
// locked, which is right on a phone and wrong on a laptop: working out the
// driving order means reading the map and the day at the same time, and
// before this you could only ever see one of them. On a computer the pin now
// opens this address in a second tab; on a phone it still opens the panel,
// because a second tab on a phone is not "side by side", it is a worse panel.
//
// The drawing itself is the SAME openDayMap the panel uses — one map, one set
// of rules about who has a postcode and who does not. This screen only feeds
// it the right day and gives it a page to sit on.

export function mountMap(date: string): void {
  const root = document.querySelector<HTMLElement>('#screen')!
  clear(root)
  document.body.classList.add('map-page')
  root.append(el('p', { class: 'muted map-loading' }, `Getting ${dayLabel(date)}…`))
  const gen = routeGen

  // The diary's own saved copy first, so a tab opened beside it draws at once
  // instead of blanking while the same day is fetched again.
  const cached = readCache<SchedulePayload>('schedule')
  if (cached && date >= cached.data.from && date <= cached.data.to) draw(cached.data, date, root)

  api<SchedulePayload>(`/api/schedule?from=${date}&to=${date}`)
    .then((p) => { if (gen === routeGen) draw(p, date, root) })
    .catch((err) => {
      if (gen !== routeGen) return
      // Something is already drawn from the saved copy: leave it up rather
      // than replacing a usable map with an error.
      if (root.querySelector('.daymap-inline')) return
      clear(root)
      loadError(root, err, () => mountMap(date))
    })
}

// The stops the van actually drives: what the day card shows, minus the
// machine's own proposals and the contact reminders, which are not visits; in the Diary's one order for the day, so
// the map's numbers are the day's (tidy-up, Liam, 20 Oct 2026).
function stopsOf(p: SchedulePayload, date: string): Clean[] {
  return diaryDayOrder(p.cleans.filter((c) => c.date === date && c.state !== 'cancelled' && !isContact(c) && !isSuggestion(c)), severalJobsOn())
}

function draw(p: SchedulePayload, date: string, root: HTMLElement): void {
  clear(root)
  const back = el('a', { href: '#/schedule', class: 'map-back' }, '← The diary')
  root.append(el('div', { class: 'map-head' }, back))
  const holder = el('div', { class: 'map-holder' })
  root.append(holder)
  openDayMap(stopsOf(p, date), dayLabel(date), holder)
}

export function unmountMap(): void {
  document.body.classList.remove('map-page')
}
